import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useAppAbility } from "@/casl";
import { Container } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { PageHeader } from "@/components/organisms";
import { Tab } from "@/components/organisms/Sidebar/types";
import { RequireAccess } from "@/routes/RequireAccess/RequireAccess";
import { Business } from "./Business/Business";
import { Marketing } from "./Marketing/Marketing";
import { Documents } from "./Documents/Documents";
import { Trip } from "./Trip/Trip";
import { UserAccounts } from "./UserAccounts/UserAccounts";

const pageTabs: Tab[] = [
  { name: "Business Settings", to: "business", icon: "" },
  { name: "Trip Settings", to: "trip", icon: "" },
  { name: "Marketing Tools", to: "marketing", icon: "" },
  { name: "Documents", to: "documents", icon: "" },
  {
    name: "User Accounts",
    to: "user-accounts",
    permission: ["manage", "providerAccountDirectory"],
    icon: "",
  },
];

export const Config = () => {
  const navigate = useNavigate();
  const ability = useAppAbility();
  const { pathname } = useLocation();
  const isParentPage = Boolean(
    matchPath({ path: "/config/:slug" }, pathname) ||
      matchPath({ path: "operator/:operatorId/config/:slug" }, pathname)
  );
  const isTripCost = pathname.includes("/config/trip/trip-cost");
  const doubleBackRoutes = ["/config/trip/hourly-daily-hire", "/config/trip/dispatch-settings/edit/offloading"];
  const isDoubleBackRoute = doubleBackRoutes.some(route => pathname.includes(route));
  
  const renderTabs = () => (
    <Tabs className="no-scrollbar">
      {pageTabs.map((t, i) =>
        t.permission
          ? ability.can(t.permission[0], t.permission[1]) && (
              <Tabs.Tab key={i} to={t.to} name={t.name} />
            )
          : <Tabs.Tab key={i} to={t.to} name={t.name} />
      )}
    </Tabs>
  );

  return (
    <>
      <ConfigHeader
        isTripCost={isTripCost}
        isParentPage={isParentPage}
        onBack={() => isDoubleBackRoute ? navigate(pathname.split("/").slice(0, -2).join("/")) : navigate(pathname.split("/").slice(0, -1).join("/"))}
        renderTabs={renderTabs}
      />
      <ConfigContent isTripCost={isTripCost} />
    </>
  );
};

type HeaderProps = {
  isTripCost: boolean;
  isParentPage: boolean;
  onBack: () => void;
  renderTabs: () => React.ReactNode;
};

const ConfigHeader = ({ isTripCost, isParentPage, onBack, renderTabs }: HeaderProps) => {
  if (isTripCost) {
    return (
      <div className="-mx-3 lg:-mx-5 border-b border-neutral-mid-gray">
        <Container className="w-full">
          <PageHeader.Actions onBack={onBack} className="pt-3 pb-0 px-3 lg:px-5" />
          <PageHeader className="!mx-0 px-3 lg:px-5" title="Trip Cost" skipCrumbs={3} />
          {isParentPage && renderTabs()}
        </Container>
      </div>
    );
  }

  return (
    <Container className="w-full">
      <PageHeader.Actions onBack={onBack} className="pt-3 pb-0" />
      <PageHeader title="Settings" skipCrumbs={1} />
      {isParentPage && (
        <Tabs className="no-scrollbar mb-5 border-b !border-neutral-mid-gray">
          {renderTabs()}
        </Tabs>
      )}
    </Container>
  );
};

const ConfigContent = ({ isTripCost }: { isTripCost: boolean }) => {
  if (isTripCost) {
    return (
      <div className="-mx-3 lg:-mx-5 bg-neutral-surface-gray">
        <div className="px-3 lg:px-5 relative">
          <Container className="w-full">
            <div className="flex flex-1 flex-col pb-12">
              <RequireAccess action="view" subject="providerConfig" />
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <Container className="w-full">
      <div className="flex flex-1 flex-col pb-12">
        <RequireAccess action="view" subject="providerConfig" />
      </div>
    </Container>
  );
};

Config.Trip = Trip;
Config.Business = Business;
Config.Marketing = Marketing;
Config.Documents = Documents;
Config.UserAccounts = UserAccounts;